import { loginRequestApi } from '@utils/authConfig.ts'
import { msalInstance } from '@utils/msal.ts'

/**
 * Utility functions for checking user permissions and roles
 */

export interface UserPermissions {
  isAdmin: boolean
  hasSensitiveReadAccess: boolean
  hasSensitiveWriteAccess: boolean
  hasSkogHubUserAccess: boolean
  hasSBSkogAccess: boolean
  hasVikenSkogAccess: boolean
  userId?: string
  userName?: string
  roles: string[]
}

/**
 * Get the current user's permissions and roles from the ID token
 */
export async function getUserPermissions(options?: {
  forceRefresh?: boolean
}): Promise<UserPermissions> {
  try {
    const accounts = msalInstance.getAllAccounts()
    if (accounts.length === 0) {
      return {
        isAdmin: false,
        hasSensitiveReadAccess: false,
        hasSensitiveWriteAccess: false,
        hasSkogHubUserAccess: false,
        hasSBSkogAccess: false,
        hasVikenSkogAccess: false,
        roles: [],
      }
    }

    const account = accounts[0]

    // Acquire token to ensure we have the latest claims
    const tokenResponse = await msalInstance.acquireTokenSilent({
      account,
      ...loginRequestApi,
      forceRefresh: options?.forceRefresh ?? false,
    })

    // Get the ID token which contains user claims
    const idToken = tokenResponse.idToken

    if (!idToken) {
      return {
        isAdmin: false,
        hasSensitiveReadAccess: false,
        hasSensitiveWriteAccess: false,
        hasSkogHubUserAccess: false,
        hasSBSkogAccess: false,
        hasVikenSkogAccess: false,
        roles: [],
      }
    }

    // Parse the ID token to extract claims
    const claims = parseJwtPayload(idToken)

    // Extract roles from the token
    const roles = extractRolesFromClaims(claims)
    const isAdmin = roles.includes('SkogHub.Administrator')

    // Returns true if user has SensitiveReader OR SensitiveWriter role
    // (Writer inherently includes Reader capabilities)
    const hasSensitiveReadAccess =
      roles.includes('SkogHub.Sensitive.Reader') || roles.includes('SkogHub.Sensitive.Writer')
    const hasSensitiveWriteAccess = roles.includes('SkogHub.Sensitive.Writer')
    const hasSkogHubUserAccess = roles.includes('SkogHub.User')
    const hasSBSkogAccess = roles.includes('SBSkog')
    const hasVikenSkogAccess = roles.includes('VikenSkog')

    return {
      isAdmin,
      hasSensitiveReadAccess,
      hasSensitiveWriteAccess,
      hasSkogHubUserAccess,
      hasSBSkogAccess,
      hasVikenSkogAccess,
      userId: (claims.sub as string) || (claims.oid as string),
      userName: (claims.name as string) || (claims.preferred_username as string),
      roles,
    }
  } catch (error) {
    console.error('Error getting user permissions:', error)
    return {
      isAdmin: false,
      hasSensitiveReadAccess: false,
      hasSensitiveWriteAccess: false,
      hasSkogHubUserAccess: false,
      hasSBSkogAccess: false,
      hasVikenSkogAccess: false,
      roles: [],
    }
  }
}

/**
 * Parse JWT payload from ID token
 */
function parseJwtPayload(token: string): Record<string, unknown> {
  try {
    const base64Url = token.split('.')[1]
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => `%${(`00${c.charCodeAt(0).toString(16)}`).slice(-2)}`)
        .join(''),
    )
    return JSON.parse(jsonPayload) as Record<string, unknown>
  } catch (error) {
    console.error('Error parsing JWT payload:', error)
    return {}
  }
}

/**
 * Extract roles from token claims
 * Roles can be in different claim types depending on the token configuration
 */
function extractRolesFromClaims(claims: Record<string, unknown>): string[] {
  const roles: string[] = []

  // Check common role claim types
  const roleClaimTypes = [
    'roles', // Standard roles claim
    'http://schemas.microsoft.com/ws/2008/06/identity/claims/role', // Legacy role claim
    'http://schemas.microsoft.com/identity/claims/role', // Another common role claim
  ]

  for (const claimType of roleClaimTypes) {
    const roleClaim = claims[claimType]
    if (roleClaim) {
      if (Array.isArray(roleClaim)) {
        roles.push(...(roleClaim as string[]))
      } else if (typeof roleClaim === 'string') {
        roles.push(roleClaim)
      }
    }
  }

  return [...new Set(roles)] // Remove duplicates
}

/**
 * Check if the current user has sensitive read access
 */
export async function hasUserSensitiveReadAccess(): Promise<boolean> {
  const permissions = await getUserPermissions()
  return permissions.hasSensitiveReadAccess
}

/**
 * Check if the current user has sensitive write access
 */
export async function hasUserSensitiveWriteAccess(): Promise<boolean> {
  const permissions = await getUserPermissions()
  return permissions.hasSensitiveWriteAccess
}

/**
 * Get user roles as a simple array
 */
export async function getUserRoles(): Promise<string[]> {
  const permissions = await getUserPermissions()
  return permissions.roles
}

/**
 * Check if the current user has SkogHub.User access
 */
export async function hasSkogHubUserAccess(): Promise<boolean> {
  const permissions = await getUserPermissions()
  return permissions.hasSkogHubUserAccess
}
